import {
  Button,
  DatePicker,
  Form,
  Stack,
  Typography,
  useDateFieldValidator,
} from '@ace-clublabs/shared-component-library';
import { Pages } from '@lib/types';
import React, { useContext, useEffect, useLayoutEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { MembershipAppStateContext } from '../components/MembershipAppState/MembershipAppState';
import dayjs from 'dayjs';
import { getNextPage } from '../utils/getNextPage';
import { FlowType } from '@ace-clublabs/common-membership-api-types';
import { UtagType } from '../utils/UtagHelper';
import { getProgressProps } from '../utils/helpers';
import { AppProgressHeader } from '../components/AppProgressHeader';
import { getJan2Date, getTodayDate, getTodayDatePlus30 } from '../utils/dateHelper';

export const DateSelector: React.FC = () => {
  const { sessionData, updateMembershipForm, refreshSession, membershipInfo, sendTag } =
    useContext(MembershipAppStateContext);
  const [isTagging, setIsTagging] = useState(true);
  const isHolidaySession = membershipInfo?.giftDetails?.isHoliday || false;
  const history = useHistory();
  const { formData } = sessionData;
  const { giftDetails } = formData;
  const endDate = giftDetails?.endDate || getJan2Date();

  const tag = () => {
    const tagPage = sessionData.flowType === FlowType.GIFT ? 'membership:gift:select date' : 'select level';
    sendTag(UtagType.view, tagPage);
    setIsTagging(false);
  };

  const dateValidator = useDateFieldValidator({
    maxDate: getMaxDate(),
    required: true,
    disablePast: true,
  });

  const [startDate, setStartDate] = useState<string | undefined>(giftDetails?.startDate || getTodayDate());

  function getMaxDate(): import('dayjs').Dayjs | undefined {
    return isHolidaySession && endDate ? dayjs(endDate) : dayjs(getTodayDatePlus30());
  }

  useLayoutEffect(() => {
    refreshSession();
    tag();
    setIsTagging(false);
  }, []);

  useEffect(() => {
    const updatedFormData = {
      ...sessionData.formData,
      giftDetails: {
        ...sessionData.formData.giftDetails,
        startDate: startDate,
      },
    };
    updateMembershipForm(updatedFormData);
  }, [startDate]);

  if (isTagging) {
    return <></>;
  }

  function nextStep(): void {
    history.push(getNextPage(Pages.DateSelector, sessionData.flowType));
  }

  const progressProps = getProgressProps(sessionData.flowType!, Pages.DateSelector);

  const errorDescription = (): string => {
    let description = '';
    const today = new Date();
    const year = today.getFullYear() + 1;
    //if in holiday mode
    if (isHolidaySession) {
      description = 'You may select any date before January 2, ' + year;
    } else {
      //if in normal mode
      description = 'You may select any date up to 30 days from today.';
    }
    return description;
  };

  return (
    <>
      <AppProgressHeader {...progressProps} styleOverrides={{ marginBottom: '1rem' }} />

      <div className="date-selector-section">
        <Stack direction={'column'}>
          <Typography className="date-selector-title" variant="h6Medium">
            Select a start date for the gift membership
          </Typography>
          <Form sx={{ marginTop: 0 }}>
            <DatePicker
              className="date-selector"
              desktopCalendarHelperText={errorDescription()}
              disablePast
              helperText="mm/dd/yyyy"
              label="Start date"
              mobileCalendarHelperText={errorDescription()}
              calendarTitle="Start date"
              placeholder={getTodayDate()}
              validator={dateValidator}
              value={dayjs(startDate)}
              onChange={(v) => {
                setStartDate(v?.format('MM/DD/YYYY') ?? undefined);
              }}
              validationErrorMessage={errorDescription()}
              sx={{
                '&:not(.MuiFormControl-fullWidth).MuiTextField-root': {
                  width: '100% !important',
                },
              }}
            />
            <Button
              sx={{ marginTop: '1.5rem' }}
              className="date-selector-button"
              label="Next"
              fullWidth
              type="submit"
              onClick={() => nextStep()}
              variant="contained"
              size="medium"
              id="nextButton"
            ></Button>
          </Form>
        </Stack>
      </div>
    </>
  );
};
